import {
  Delete as DeleteIcon,
  Folder as DocumentsFilledIcon,
  FolderOutlined as DocumentsIcon,
  PlayArrow as RunIcon,
  Assessment as ResearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Stop as StopIcon,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "material-react-table";
import React, { useMemo } from "react";
import {
  EvaluationStatus,
  ResearchEntity,
} from "../../../types/researchWorkflow";
import { getScoreChipSx } from "../shared/scoreColors";
import { selectVisibleDeselectAllProps } from "../shared/selectionProps";
import EvaluationScoreTooltip from "./EvaluationScoreTooltip";

interface ShortListTableViewProps {
  entities: ResearchEntity[];
  isLoading?: boolean;
  rowSelection: MRT_RowSelectionState;
  onRowSelectionChange: (
    updater:
      | MRT_RowSelectionState
      | ((old: MRT_RowSelectionState) => MRT_RowSelectionState),
  ) => void;
  onEntityClick?: (entityId: string) => void;
  onSelectEntity: (entityId: string) => void;
  onMoveToFinal: (entityId: string) => void;
  onRemoveFromShortList: (entityId: string) => void;
  onViewEvaluation: (entity: ResearchEntity) => void;
  onCancelEvaluation: (entityId: string) => void;
  onTriggerEvaluation: (entityId: string) => void;
  onManageDocuments: (entity: ResearchEntity) => void;
  isViewer?: boolean;
}

// Per-candidate evaluation status chip presentation.
const EVAL_STATUS_CHIP: Record<
  Exclude<EvaluationStatus, "none">,
  { label: string; color: "default" | "info" | "warning" | "success" | "error" }
> = {
  queued: { label: "Queued", color: "info" },
  running: { label: "Evaluating", color: "warning" },
  completed: { label: "Evaluated", color: "success" },
  failed: { label: "Failed", color: "error" },
  cancelled: { label: "Cancelled", color: "default" },
};

const ShortListTableView: React.FC<ShortListTableViewProps> = ({
  entities,
  isLoading = false,
  rowSelection,
  onRowSelectionChange,
  onEntityClick,
  onSelectEntity,
  onMoveToFinal,
  onRemoveFromShortList,
  onViewEvaluation,
  onCancelEvaluation,
  onTriggerEvaluation,
  onManageDocuments,
  isViewer = false,
}) => {
  const columns = useMemo<MRT_ColumnDef<ResearchEntity>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
        Cell: ({ cell, row }) => (
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "primary.light",
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onEntityClick) {
                onEntityClick(row.original.kg_entity_id);
              } else {
                onSelectEntity(row.original.id);
              }
            }}
          >
            {String(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "home_country",
        header: "Home Country",
        size: 140,
        Cell: ({ cell, row }) => {
          const country =
            (cell.getValue() as string | undefined) ||
            row.original.country_iso2;
          return country ? (
            <Typography variant="body2">{country}</Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        id: "evaluationScore",
        accessorFn: (row) => row.evaluationData?.overall_score ?? -1,
        header: "Score",
        size: 80,
        Cell: ({ row }) => {
          const entity = row.original;
          const evaluationScore = entity.evaluationData?.overall_score;
          const displayScore =
            evaluationScore !== undefined ? evaluationScore : undefined;

          const getScoreChipColor = getScoreChipSx;

          const evaluationData = entity.evaluationData as any;

          const chip = (
            <Chip
              label={displayScore !== undefined ? displayScore.toFixed(1) : "-"}
              size="small"
              sx={{
                fontWeight: 600,
                minWidth: 44,
                ...getScoreChipColor(displayScore),
                ...(displayScore !== undefined && {
                  cursor: "pointer",
                  "&:hover": { opacity: 0.85 },
                }),
              }}
              onClick={
                displayScore !== undefined
                  ? (e) => {
                      e.stopPropagation();
                      onViewEvaluation(entity);
                    }
                  : undefined
              }
            />
          );

          if (displayScore === undefined) return chip;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EvaluationScoreTooltip evaluationData={evaluationData}>
                {chip}
              </EvaluationScoreTooltip>
              {evaluationData?.spec_version_changed && (
                <WarningAmber sx={{ fontSize: 16, color: "warning.main" }} />
              )}
            </Box>
          );
        },
      },
      {
        id: "evaluationStatus",
        accessorFn: (row) => row.evaluationStatus ?? "none",
        header: "Evaluation",
        size: 130,
        Cell: ({ row }) => {
          const st = (row.original.evaluationStatus ?? "none") as EvaluationStatus;
          if (st === "none") {
            return (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            );
          }
          const chip = EVAL_STATUS_CHIP[st];
          return (
            <Chip
              label={chip.label}
              size="small"
              color={chip.color}
              icon={
                st === "running" ? (
                  <CircularProgress size={12} color="inherit" />
                ) : undefined
              }
              sx={{ fontWeight: 500 }}
            />
          );
        },
      },
      {
        id: "evaluationRunAt",
        accessorFn: (row) => row.evaluationRunAt ?? "",
        header: "Last Run",
        size: 170,
        Cell: ({ row }) => {
          const at = row.original.evaluationRunAt;
          return at ? (
            <Typography variant="body2">
              {new Date(at).toLocaleString()}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 220,
        Cell: ({ row }) => {
          const entity = row.original;
          const isFinal = entity.workflowStatus === "final";
          const evalStatus = entity.evaluationStatus ?? "none";
          const evalInFlight =
            evalStatus === "queued" || evalStatus === "running";
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {entity.deepResearchStatus === "complete" &&
                entity.evaluationData && (
                  <Tooltip title="View Evaluation Results">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => onViewEvaluation(entity)}
                    >
                      <ResearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              {!isViewer &&
                (evalInFlight ? (
                  <Tooltip title="Cancel evaluation">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onCancelEvaluation(entity.id)}
                    >
                      <StopIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={
                      evalStatus === "none"
                        ? "Evaluate this candidate"
                        : "Re-evaluate this candidate"
                    }
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onTriggerEvaluation(entity.id)}
                    >
                      <RunIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ))}
              {!isViewer && (
                <>
                  <Tooltip
                    title={
                      entity.documentCount
                        ? `Manage documents (${entity.documentCount} linked)`
                        : "Manage documents for this candidate"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => onManageDocuments(entity)}
                    >
                      {entity.documentCount ? (
                        <DocumentsFilledIcon fontSize="small" />
                      ) : (
                        <DocumentsIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      isFinal ? "In Final Selection" : "Move to Final Selection"
                    }
                  >
                    <IconButton
                      size="small"
                      color={isFinal ? "warning" : "default"}
                      onClick={() => !isFinal && onMoveToFinal(entity.id)}
                      sx={isFinal ? { cursor: "default" } : undefined}
                    >
                      {isFinal ? (
                        <StarIcon fontSize="small" />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from Evaluation">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemoveFromShortList(entity.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        },
      },
    ],
    [
      onEntityClick,
      onSelectEntity,
      onMoveToFinal,
      onRemoveFromShortList,
      onViewEvaluation,
      onCancelEvaluation,
      onTriggerEvaluation,
      onManageDocuments,
      isViewer,
    ],
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={entities}
      enableColumnResizing
      enableStickyHeader
      enableBottomToolbar
      enableTopToolbar={false}
      enableRowSelection
      // Select adds the visible page; a fully-selected page clears the whole
      // id-keyed selection, including rows parked on other pages.
      muiSelectAllCheckboxProps={selectVisibleDeselectAllProps(
        onRowSelectionChange,
      )}
      getRowId={(row) => row.id}
      onRowSelectionChange={onRowSelectionChange}
      // Keep the user on their current page when polling refetches data; the
      // table remounts (→ page 1) when the stage/view is switched. See #888.
      autoResetPageIndex={false}
      state={{
        isLoading: isLoading,
        showProgressBars: isLoading,
        rowSelection,
      }}
      initialState={{
        density: "compact",
        pagination: { pageSize: 20, pageIndex: 0 },
        sorting: [{ id: "evaluationScore", desc: true }],
      }}
      muiTablePaperProps={{
        elevation: 0,
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
        },
      }}
      muiTableContainerProps={{
        sx: { flex: 1 },
      }}
      muiBottomToolbarProps={{
        sx: { backgroundColor: "#1e1e1e" },
      }}
    />
  );
};

export default ShortListTableView;
